import * as React from 'react';
import { Box, Button, Divider, Menu, Stack } from '@mui/material';
import { ArrowDropIcon } from '../../../../../../icons/ArrowDropIcon';
import { SpeechVoiceSelectHOC } from '../../../SpeechVoiceSelect/hocs/SpeechVoiceSelectHOC';
import { IPlaybackSettingsProps } from './types';
import { theme } from '../../../../../common/styles/theme';
import { PauseMouseIcon } from '../../../../../../icons/PauseMouseIcon';
import { ControlSwitch } from '../../../Controls/component/ControlSwitch';
import { PLAY_RATE_LIST } from '../constants';
import { Text } from '../../../../../common/components/Text/Text';

export const PlaybackSettings: React.FC<IPlaybackSettingsProps> = ({
  anchor,
  hoverAutoPause,
  rate,
  autoSpeechRate,
  canSelectVoice,
  onClose,
  onHoverAutoPauseChange,
  onRateChange,
  onAutoSpeechRateChange,
}) => {

  const className = 'playback-settings';
  const [voiceSelect, setVoiceSelect] = React.useState(false);

  const textBoldStyle = {
    fontWeight: 'bold',
    pt: theme.spacing(1),
  };

  const element = (
    <Stack
      direction={'column'}
      sx={{
        px: theme.spacing(2),

        [`.${className}__switch`]: {
          position: 'relative',
          width: 1,

          '.MuiFormControlLabel-root': {
            width: 1,
          },

          '.lang-flag': {
            position: 'absolute',
            left: theme.spacing(-3),
            top: theme.spacing(1.25),
            opacity: .3,
          },

          '&.checked': {
            [`.${className}__switch-icon`]: {
              color: theme.palette.primary.main,
            },
            '.lang-flag': {
              opacity: 1,
            },
          },
        },

        [`.${className}__switch-icon`]: {
          position: 'absolute',
          left: theme.spacing(-3),
          top: theme.spacing(2),
          color: theme.palette.grey[200],
          transition: 'all .3s ease',
        },

      }}
    >
        <Text
          sx={{
            fontSize: theme.typography.subtitle2,
            fontWeight: 600,
          }}
        >
          Playback settings
        </Text>
        <Box sx={textBoldStyle}>Player speed</Box>
        <Stack
          direction="row"
          gap={theme.spacing(0.5)}
          sx={{
            mt: theme.spacing(0.5),
          }}
        >
          {PLAY_RATE_LIST.map((rt) => (
            <Button
              key={rt}
              variant={rt === rate ? 'contained' : 'outlined'}
              sx={{
                px: 1,
                height: theme.spacing(4),
                fontSize: theme.typography.subtitle1,
              }}
              onClick={() => onRateChange(rt)}
            >
              {rt === 1 ? 'Normal' : '×' + rt}
            </Button>
          ))}
        </Stack>

        <Box
          className={`${className}__switch ${hoverAutoPause ? 'checked' : ''}`}
        >
          <PauseMouseIcon
            className={`${className}__switch-icon`}
          />

          <ControlSwitch
            title="Pause when mouse is in subtitle area"
            subTitle="Stops video when the cursor hovers over subtitles area"
            checked={hoverAutoPause}
            onChange={onHoverAutoPauseChange}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={textBoldStyle}>Auto speech speed</Box>
        <Stack
          direction="row"
          gap={theme.spacing(0.5)}
          sx={{
            mt: theme.spacing(0.5),
          }}
        >
          {PLAY_RATE_LIST.map((rt) => (
            <Button
              key={rt}
              variant={rt === autoSpeechRate ? 'contained' : 'outlined'}
              sx={{
                px: 1,
                height: theme.spacing(4),
                fontSize: theme.typography.subtitle1,
              }}
              onClick={() => onAutoSpeechRateChange(rt)}
            >
              {rt === 1 ? 'Normal' : '×' + rt}
            </Button>
          ))}
          {canSelectVoice &&
          <Button
            className={voiceSelect ? 'active' : ''}
            variant={'text'}
            sx={{
              px: 1,
              height: theme.spacing(4),
              color: theme.palette.grey[500],
              fontSize: theme.typography.subtitle2,
              '&.active': {
                color: theme.palette.grey[900],
                background: theme.palette.grey[50],
                '.MuiSvgIcon-root': {
                  transform: 'rotate(180deg)',
                },
              },
            }}
            endIcon={<ArrowDropIcon />}
            onClick={() => setVoiceSelect(!voiceSelect)}
          >
            Select Voice
          </Button>
          }
        </Stack>
        {voiceSelect && <SpeechVoiceSelectHOC />}

      </Stack>
  );

  return (
    <Menu
      id="account-menu"
      anchorEl={anchor}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={true}
      onClose={onClose}
      sx={{
        '.MuiPaper-root': {
          width: theme.spacing(46),
          boxShadow: theme.shadows[16],
        },

        '.MuiList-root': {
          p: theme.spacing(2),
        },
      }}
    >
      {element}
    </Menu>
  );
};
